import { Button, Modal, SimpleTable, Input } from "@components";
import { cn, formatAmount, formatPascalCase, formatDateTime } from "@utils";
import { useState, useEffect } from "react";
import {
  addLedgerPaymentAllocation,
  deleteLedgerPaymentAllocation,
  getCustomerLedgerTransactions,
} from "@api";
import { Trash } from "react-bootstrap-icons";

const LedgerPaymentEdit = ({ payment, onUpdate }) => {
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [transactions, setTransactions] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    async function fetchData() {
      const data = await getCustomerLedgerTransactions(payment.accountCode);
      setTransactions(data);
    }

    if (!transactions) {
      fetchData();
    }

    if (searchTerm) {
      const available = transactions.filter(
        (item) =>
          !payment.allocations.some(
            (y) => y.transactionNumber === item.transactionNumber,
          ),
      );
      const matches = available.filter((item) =>
        item.transactionNumber.includes(searchTerm),
      );
      setFiltered(matches);
    } else {
      setFiltered([]);
    }
  }, [searchTerm]);

  const reload = async () => {
    const data = await getCustomerLedgerTransactions(payment.accountCode);
    setTransactions(data);
  };

  const deleteTask = async () => {
    setSubmitting(true);
    await deleteLedgerPaymentAllocation(payment.id, deleting);
    await reload();
    setDeleting(null);
    setSubmitting(false);
    onUpdate();
  };

  const allocate = async (id) => {
    await addLedgerPaymentAllocation(payment.id, id);
    await reload();
    setSearchTerm("");
    setFiltered([]);
    onUpdate();
  };

  const dataSet = {
    columns: [
      {
        title: "Type",
        width: "120px",
        render: (x) => formatPascalCase(x.transactionType),
      },
      {
        title: "Amount",
        width: "120px",
        render: (x) => formatAmount(x.amount),
        alignment: "right",
      },
      {
        title: "Reference",
        width: "120px",
        render: (x) => x.transactionNumber,
        type: "string",
      },
      {
        title: "Transaction date",
        width: "120px",
        render: (x) => formatDateTime(x.transactionDate),
        type: "string",
      },
      {
        title: "",
        width: "auto",
        render: (x) => (
          <button
            onClick={() => setDeleting(x.id)}
            className="bg-white p-2 border-0"
          >
            <Trash color="red" className="btn btn-blue" />
          </button>
        ),
      },
    ],
  };

  return (
    <>
      <div className="px-5 mt-3 border-b pb-5">
        <label>Add new item</label>

        <div className="relative w-full mt-2">
          <Input
            placeholder="Search customer invoices and credit notes"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
          />
          <div className="relative">
            {filtered && filtered.length > 0 && (
              <div className="absolute text-sm overflow-y-auto w-full bg-white border srollbar-hidden z-50">
                {filtered.map((x) => (
                  <div
                    key={x.id}
                    role="button"
                    onClick={() => allocate(x.id)}
                    color="outline-secondary"
                    className="p-3 flex hover:bg-zinc-50"
                  >
                    <div>
                      {" "}
                      {x.type} #{x.transactionNumber}
                    </div>
                    <div className="ml-auto">
                      {formatAmount(x.remainingAmount)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-between text-gray-600 px-5 mt-3">
        <div className="text-sm leading-8">
          Payment made
          <p className="text-2xl text-black">{formatAmount(payment.amount)}</p>
        </div>
        <div className="text-sm  leading-8">
          Allocated value
          <p
            className={cn(
              "text-2xl",
              payment.unallocated < 0 ? "text-black" : "text-red-500",
            )}
          >
            {formatAmount(payment.amount - payment.unallocated)}
          </p>
        </div>
        <div className="text-sm leading-8">
          Unallocated value
          <p className="text-2xl text-black">
            {formatAmount(payment.unallocated)}
          </p>
        </div>
      </div>

      <div className="max-h-[40vh] overflow-y-auto mt-1">
        <SimpleTable config={dataSet} data={payment.allocations} padding={0} />
      </div>

      <Modal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        title="Remove task "
      >
        <p className="px-5 py-5">Are you sure, you want to delete this task?</p>
        <div className="p-4 flex gap-2 justify-end border-t">
          <Button onClick={() => setDeleting(null)} color="secondary">
            Back
          </Button>
          <Button
            submitting={submitting}
            onClick={() => deleteTask()}
            color="danger"
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default LedgerPaymentEdit;
